// ============================================================================
// PROTOTYPE — MOCK ESTIMATE SERVICE.
// Every screen reads/writes through these async functions ONLY. Swap each one
// for a real axios call (see README.md → "Replacing the mock service") and the
// UI keeps working unchanged. State lives in memory + sessionStorage so a page
// reload during a demo keeps the estimate you just built.
// Add `?demo=error` to any estimate URL to make every call fail.
// ============================================================================

import { ACTIVE_CONFIG_ID, CONFIG_VERSIONS, buildConfig } from "./config.mock";
import { RAW_TAKEOFF_2918 } from "./takeoff.mock";
import { JOBS, DEFAULT_PROJECT, DEFAULT_INCLUSIONS, DEFAULT_EXCLUSIONS, buildSeedEstimates } from "./estimates.mock";
import { parseRawLine, applyRememberedMappings } from "../engine/suggestSurface";
import { calculateEstimate, toTotalsSnapshot } from "../engine/calcMock";

const STORAGE_KEY = "sb_estimate_prototype_v1";
const clone = (v) => JSON.parse(JSON.stringify(v));
const nowIso = () => new Date().toISOString();

// ---------------------------------------------------------------- store ----
// Older versions differ slightly so "Compare with previous" has something to show.
const configForVersion = (id) => {
  const cfg = buildConfig(id);
  if (id === "v12") return cfg;
  const priceFactor = id === "v11" ? 0.97 : 0.94;
  cfg.products = cfg.products
    .filter((p) => id !== "v10" || p.manufacturer !== "Benjamin Moore")
    .map((p) => ({ ...p, pricePerGal: +(p.pricePerGal * priceFactor).toFixed(2), pricePerQt: +(p.pricePerQt * priceFactor).toFixed(2), pricePer5Gal: +(p.pricePer5Gal * priceFactor).toFixed(2) }));
  cfg.laborBuildup = { ...cfg.laborBuildup, items: cfg.laborBuildup.items.map((i) => (i.key === "wc" ? { ...i, pct: 0.11 } : i)) };
  return cfg;
};

const createStore = () => {
  const configs = Object.fromEntries(CONFIG_VERSIONS.map((v) => [v.id, configForVersion(v.id)]));
  return {
    versions: clone(CONFIG_VERSIONS),
    configs,
    activeConfigId: ACTIVE_CONFIG_ID,
    estimates: buildSeedEstimates(configs[ACTIVE_CONFIG_ID]),
    rememberedMappings: {},
    nextEstimateNo: 1005,
  };
};

let store = null;
const getStore = () => {
  if (store) return store;
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) store = JSON.parse(saved);
  } catch {
    store = null;
  }
  if (!store) store = createStore();
  return store;
};
const persist = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // storage full / blocked — the in-memory store still works for this tab
  }
};

/** Resets all prototype data (used by the "Reset demo data" link on the list page). */
export const resetMockData = async () => {
  store = createStore();
  persist();
  return true;
};

// -------------------------------------------------------------- helpers ----
const isDemoError = () => {
  try {
    return new URLSearchParams(window.location.search).get("demo") === "error";
  } catch {
    return false;
  }
};

const latency = (min = 400, max = 1200) => new Promise((r) => setTimeout(r, min + Math.random() * (max - min)));

/** Wraps every mock call: fake latency + the ?demo=error switch. */
const call = async (fn, opts) => {
  await latency(opts?.min, opts?.max);
  if (isDemoError()) throw new Error("The estimate service is unavailable (demo=error).");
  return clone(fn());
};

const findEstimate = (id) => {
  const est = getStore().estimates.find((e) => e.id === id);
  if (!est) throw new Error(`Estimate ${id} not found`);
  return est;
};

const pushHistory = (est, type, user, totals, note) => {
  est.history.unshift({ id: `${est.id}-h${Date.now()}`, type, user: user || "You", at: nowIso(), totals, note });
};

// ----------------------------------------------------------------- jobs ----

/** Sync helper for entry-point buttons. Real API: `project.has_takeoff_file`. */
export const hasTakeoffFile = (fileId) => !!JOBS[Number(fileId)]?.takeoffFile;

/** @returns {Promise<import("../types").JobSummary>} */
export const getJob = (jobId) =>
  call(() => JOBS[Number(jobId)] || { jobId: Number(jobId), title: `Job ${jobId}`, clientName: "—", takeoffFile: null });

// ------------------------------------------------------ background jobs ----
// Real API: POST starts a job and returns { runId }; GET /jobs/:runId is polled.
const runs = {};

const startRun = (stages, durationMs, resultFn) => {
  const runId = `run_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  runs[runId] = { stages, startedAt: Date.now(), durationMs, resultFn, result: undefined };
  return { runId };
};

/** @returns {Promise<import("../types").JobProgress>} */
export const getJobProgress = async (runId) => {
  await latency(80, 160);
  const run = runs[runId];
  if (!run) throw new Error("Job not found");
  const elapsed = Date.now() - run.startedAt;
  const pct = Math.min(100, (elapsed / run.durationMs) * 100);
  const stageIndex = Math.min(run.stages.length - 1, Math.floor((pct / 100) * run.stages.length));
  const done = pct >= 100;
  if (done && run.result === undefined) run.result = run.resultFn();
  return clone({ runId, stages: run.stages, stageIndex: done ? run.stages.length : stageIndex, pct, done, result: done ? run.result : undefined });
};

export const PARSE_STAGES = ["Reading file", "Extracting lines", "Detecting units", "Suggesting surfaces"];
export const CALC_STAGES = ["Line calcs", "Equipment", "General conditions", "Materials", "Bid"];

/** Starts the "Load & Parse" job. Result: ParseResult. */
export const startParseTakeoff = (jobId) =>
  call(() => {
    if (!JOBS[Number(jobId)]?.takeoffFile) throw new Error("This job has no takeoff file.");
    return startRun(PARSE_STAGES, 3000, () => {
      const lines = applyRememberedMappings(RAW_TAKEOFF_2918.map(parseRawLine), getStore().rememberedMappings);
      const noQty = lines.filter((l) => l.warnings.includes("No quantity")).length;
      const badUnit = lines.filter((l) => l.warnings.some((w) => w.startsWith("Unknown unit"))).length;
      const warnings = [];
      if (noQty) warnings.push(`${noQty} line${noQty > 1 ? "s have" : " has"} no quantity`);
      if (badUnit) warnings.push(`${badUnit} unknown unit${badUnit > 1 ? "s" : ""}`);
      if (noQty + badUnit) warnings.push(`${noQty + badUnit} line${noQty + badUnit > 1 ? "s" : ""} will need review before calculating`);
      return {
        lines,
        stats: { lineCount: lines.length, sectionCount: new Set(lines.map((l) => l.section)).size, warnings },
      };
    });
  }, { min: 300, max: 500 });

/**
 * Starts the "Calculate Estimate" job: creates the estimate server-side.
 * @param {{jobId:number, project:Object, lines:import("../types").EstimateLine[], user:string}} payload
 * Result: { estimateId }
 */
export const startCalculation = (payload) =>
  call(() =>
    startRun(CALC_STAGES, 2000, () => {
      const s = getStore();
      const job = JOBS[Number(payload.jobId)];
      const config = s.configs[s.activeConfigId];
      const calc = calculateEstimate({ lines: payload.lines, project: payload.project }, config);
      const totals = toTotalsSnapshot(calc);
      const id = `EST-${s.nextEstimateNo++}`;
      const at = nowIso();
      s.estimates.unshift({
        id,
        jobId: Number(payload.jobId),
        jobTitle: job?.title || `Job ${payload.jobId}`,
        clientName: job?.clientName || "—",
        status: "DRAFT",
        configVersionId: s.activeConfigId,
        project: payload.project,
        lines: payload.lines,
        equipmentOverrides: {},
        gcOverrides: {},
        inclusions: DEFAULT_INCLUSIONS,
        exclusions: DEFAULT_EXCLUSIONS,
        createdBy: payload.user,
        createdAt: at,
        updatedAt: at,
        lastCalculatedAt: at,
        totals,
        history: [{ id: `${id}-h1`, type: "CREATED", user: payload.user, at, totals, note: `Created from takeoff (${payload.lines.length} lines)` }],
      });
      persist();
      return { estimateId: id };
    })
  , { min: 300, max: 500 });

// --------------------------------------------------------------- config ----

/** @returns {Promise<Object>} full config for a version (default: active). */
export const getConfig = (versionId) =>
  call(() => {
    const s = getStore();
    const id = versionId || s.activeConfigId;
    const cfg = s.configs[id] || s.configs[s.activeConfigId];
    const meta = s.versions.find((v) => v.id === cfg.versionId);
    return { ...cfg, versionLabel: `${meta?.label || cfg.versionId} – Surya Group default`, status: meta?.status };
  });

export const getDefaultProject = () => call(() => ({ ...DEFAULT_PROJECT }), { min: 200, max: 400 });

export const listConfigVersions = () => call(() => getStore().versions);

/**
 * Saves one config section ("globalAssumptions", "surfaces", ...) into a DRAFT
 * version, creating the draft from the active version on first edit.
 */
export const saveConfigSection = (section, value) =>
  call(() => {
    const s = getStore();
    let draft = s.versions.find((v) => v.status === "DRAFT");
    if (!draft) {
      const nextNo = Math.max(...s.versions.map((v) => v.version)) + 1;
      draft = { id: `v${nextNo}`, version: nextNo, label: `Config v${nextNo}`, status: "DRAFT", publishedBy: null, publishedAt: null, notes: "Draft – unpublished edits" };
      s.versions.push(draft);
      s.configs[draft.id] = { ...clone(s.configs[s.activeConfigId]), versionId: draft.id };
    }
    s.configs[draft.id][section] = value;
    persist();
    return draft;
  }, { min: 300, max: 600 });

/** Publishes the draft version: it becomes ACTIVE, the old active is ARCHIVED. */
export const publishConfigDraft = (user) =>
  call(() => {
    const s = getStore();
    const draft = s.versions.find((v) => v.status === "DRAFT");
    if (!draft) throw new Error("There is no draft version to publish.");
    s.versions.forEach((v) => { if (v.status === "ACTIVE") v.status = "ARCHIVED"; });
    Object.assign(draft, { status: "ACTIVE", publishedBy: user || "Admin", publishedAt: nowIso(), notes: "Published from Estimate Settings" });
    s.activeConfigId = draft.id;
    persist();
    return draft;
  });

/** Remembers raw-name → surface mappings for the next takeoff parse. */
export const saveMappings = (mappings) =>
  call(() => {
    const s = getStore();
    Object.assign(s.rememberedMappings, mappings);
    persist();
    return { saved: Object.keys(mappings).length };
  }, { min: 200, max: 400 });

// ------------------------------------------------------------ estimates ----

/** @returns {Promise<{data: Object[], total: number}>} list rows (no lines). */
export const listEstimates = ({ search = "", status = "ALL", skip = 0, limit = 10 } = {}) =>
  call(() => {
    const q = search.trim().toLowerCase();
    const rows = getStore()
      .estimates.filter((e) => status === "ALL" || e.status === status)
      .filter((e) => !q || [e.id, e.jobTitle, e.clientName, e.project.projectName, String(e.jobId)].some((v) => v.toLowerCase().includes(q)))
      .map(({ lines, history, ...rest }) => ({ ...rest, lineCount: lines.length }));
    return { data: rows.slice(skip, skip + limit), total: rows.length };
  });

/** @returns {Promise<import("../types").Estimate>} */
export const getEstimate = (id) => call(() => findEstimate(id));

const applyPatch = (est, patch, totals) => {
  ["project", "lines", "equipmentOverrides", "gcOverrides", "inclusions", "exclusions"].forEach((k) => {
    if (patch[k] !== undefined) est[k] = clone(patch[k]);
  });
  if (totals) est.totals = totals;
  est.updatedAt = nowIso();
};

/** Save Draft. @param patch partial Estimate, @param totals TotalsSnapshot */
export const saveDraft = (id, patch, totals, user) =>
  call(() => {
    const est = findEstimate(id);
    if (est.status === "FINALIZED") throw new Error("Finalized estimates are locked.");
    applyPatch(est, patch, totals);
    pushHistory(est, "DRAFT_SAVED", user, totals);
    persist();
    return est;
  }, { min: 400, max: 800 });

/** Recalculate — logs a history entry with the fresh totals. */
export const logRecalculation = (id, totals, user) =>
  call(() => {
    const est = findEstimate(id);
    est.lastCalculatedAt = nowIso();
    est.totals = totals;
    pushHistory(est, "RECALCULATED", user, totals);
    persist();
    return est;
  }, { min: 500, max: 900 });

export const finalizeEstimate = (id, patch, totals, user) =>
  call(() => {
    const est = findEstimate(id);
    applyPatch(est, patch, totals);
    est.status = "FINALIZED";
    pushHistory(est, "FINALIZED", user, totals, "Locked for bid submission");
    persist();
    return est;
  }, { min: 600, max: 1100 });

/** Duplicate to revise — new DRAFT copy of a (usually finalized) estimate. */
export const duplicateEstimate = (id, user) =>
  call(() => {
    const s = getStore();
    const src = findEstimate(id);
    const newId = `EST-${s.nextEstimateNo++}`;
    const at = nowIso();
    const copy = {
      ...clone(src),
      id: newId,
      status: "DRAFT",
      configVersionId: s.activeConfigId,
      createdBy: user,
      createdAt: at,
      updatedAt: at,
      lastCalculatedAt: at,
      history: [{ id: `${newId}-h1`, type: "DUPLICATED", user, at, totals: src.totals, note: `Revision of ${src.id}` }],
    };
    s.estimates.unshift(copy);
    persist();
    return copy;
  });

/** Export ▾ (PDF Bid / Excel / BOQ CSV) — queued, nothing is generated. */
export const exportEstimate = (id, format) => call(() => ({ queued: true, id, format }), { min: 300, max: 600 });

export const sendToSupplier = (id, supplier) => call(() => ({ sent: true, id, supplier }));
